import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { createDividendReinvestment, completeDividendWithoutReinvest } from '../../composables/fetchAPI';

interface ReinvestDividendModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: any;
  portfolioId: string;
  onSuccess?: () => void; // เอาไว้สั่ง Refresh ข้อมูลหลังทำรายการเสร็จ
}

const ReinvestDividendModal: React.FC<ReinvestDividendModalProps> = ({ isOpen, onClose, transaction, portfolioId, onSuccess }) => {
  const [buyPrice, setBuyPrice] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // ดึงยอดเงินปันผลที่ใช้ได้ (ดูจาก cashEffect หรือ netAmount)
  const dividendAmount = useMemo(() => {
    if (!transaction) return 0;
    return Math.abs(transaction.cashEffect ?? transaction.netAmount ?? 0);
  }, [transaction]);

 // คำนวณจำนวนหุ้นอัตโนมัติ (Quantity = Dividend Amount / Buy Price)
  const calculatedQuantity = useMemo(() => {
    const priceNum = parseFloat(buyPrice);
    if (!priceNum || priceNum <= 0) return 0;
    
    // คำนวณจำนวนหุ้นดิบ
    const rawQuantity = dividendAmount / priceNum;
    
    // ปัดลง (Floor) ให้เหลือทศนิยมสูงสุด 6 ตำแหน่ง ป้องกันทศนิยมล้นและซื้อเกินเงินที่มี
    return Math.floor(rawQuantity * 1000000) / 1000000;
  }, [buyPrice, dividendAmount]);

  if (!isOpen || !transaction) return null;

  // 1. ฟังก์ชันยืนยันลงทุนซ้ำ (DRIP)
  const handleReinvest = async () => {
    const priceNum = parseFloat(buyPrice);
    if (!priceNum || priceNum <= 0) {
      toast.error('กรุณาระบุราคาซื้อให้ถูกต้อง');
      return;
    }

    setIsProcessing(true);
    try {
      // เรียกใช้ Endpoint ใหม่สำหรับการทำ Reinvestment
      await createDividendReinvestment(portfolioId, {
        type: 'BUY',
        subPortId: transaction.subPortId,
        ticker: transaction.symbol || transaction.assetSymbol,
        quantity: calculatedQuantity,
        price: priceNum,
        fee: 0, // สันนิษฐานว่าไม่มีค่าธรรมเนียมเพิ่ม หรือจะทำช่องให้กรอกเพิ่มก็ได้
        source: 'DIVIDEND_REINVESTMENT',
        sourceTransactionId: transaction.transactionId
      });

      toast.success(`ลงทุนซ้ำหุ้น ${transaction.symbol} สำเร็จ!`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.th_message || error.message || 'เกิดข้อผิดพลาดในการลงทุนซ้ำ');
    } finally {
      setIsProcessing(false);
    }
  };

  // 2. ฟังก์ชันจบรายการโดยไม่ลงทุนซ้ำ (เก็บเป็นเงินสด)
  const handleKeepAsCash = async () => {
    const confirmKeepCash = window.confirm('คุณต้องการเก็บปันผลก้อนนี้ไว้เป็นเงินสด และไม่ต้องการให้ระบบแจ้งเตือนการลงทุนซ้ำอีก ใช่หรือไม่?');
    if (!confirmKeepCash) return;

    setIsProcessing(true);
    try {
      await completeDividendWithoutReinvest(portfolioId, transaction.transactionId, "User chose to keep dividend as cash");
      toast.success('บันทึกการเก็บเป็นเงินสดเรียบร้อยแล้ว');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.th_message || 'เกิดข้อผิดพลาดในการทำรายการ');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fadeIn p-4">
      <div className="bg-[#18181b] border border-blue-500/30 w-full max-w-md rounded-2xl shadow-[0_0_40px_rgba(59,130,246,0.15)] p-6 text-white space-y-5">
        
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-gray-800 pb-3">
          <span className="text-3xl drop-shadow-md">🔄</span>
          <div>
            <h3 className="text-lg font-bold text-blue-400">นำปันผลไปลงทุนซ้ำ (DRIP)</h3>
            <p className="text-xs text-gray-400">สำหรับหุ้น {transaction.symbol}</p>
          </div>
        </div>
        
        {/* Info Box */}
        <div className="bg-black/40 p-4 rounded-xl border border-gray-800 text-center flex flex-col items-center">
          <div className="text-xs text-gray-400 mb-1">ยอดเงินปันผลที่นำไปลงทุนได้</div>
          <div className="text-3xl font-black text-green-400 drop-shadow-sm">
            ${dividendAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>

        {/* Input Area */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-300 mb-1">
              ราคาที่เข้าซื้อ (Buy Price) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
              <input
                type="number"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                disabled={isProcessing}
                placeholder="0.00"
                className="w-full bg-gray-900 border border-gray-700 focus:border-blue-500 rounded-lg px-8 py-3 text-white outline-none transition-colors disabled:opacity-50"
              />
            </div>
          </div>

          {/* Auto Calculation Result */}
          <div className="flex items-center justify-between bg-blue-900/20 border border-blue-500/30 p-3 rounded-lg">
            <span className="text-xs font-medium text-blue-200">จำนวนหุ้นที่จะได้รับ<br/>(Calculated Quantity)</span>
            <span className="text-lg font-bold text-blue-400">
              {calculatedQuantity > 0 ? calculatedQuantity.toFixed(6) : '0.000000'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 pt-3 border-t border-gray-800">
          <button 
            onClick={handleReinvest} 
            disabled={isProcessing || !buyPrice || parseFloat(buyPrice) <= 0}
            className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-bold rounded-xl shadow-lg transition-all active:scale-[0.98]"
          >
            {isProcessing ? 'กำลังประมวลผล...' : '✅ ยืนยันเข้าซื้อเพื่อลงทุนซ้ำ'}
          </button>

          <button 
            onClick={handleKeepAsCash} 
            disabled={isProcessing}
            className="w-full py-2 bg-transparent hover:bg-gray-800 text-gray-400 text-xs font-bold rounded-lg transition-colors border border-transparent hover:border-gray-700"
          >
            ❌ ไม่ลงทุนซ้ำ (เก็บเป็นเงินสด)
          </button>

          <button 
            onClick={onClose} 
            disabled={isProcessing}
            className="w-full py-2 text-gray-500 hover:text-gray-300 text-xs underline"
          >
            ปิดหน้าต่างนี้
          </button>
        </div>

      </div>
    </div>
  );
};

export default ReinvestDividendModal;